import React, { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CHUNK_SIZE = 1.5 * 1024 * 1024;

export default function UploadField({ value, onChange, accept = "image", required = false, testId = "upload-field" }) {
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);
  const acceptAttr = accept === "video" ? "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" : "image/*";

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) {
      toast.error("File too large (max 100MB)");
      return;
    }
    const total = Math.max(1, Math.ceil(f.size / CHUNK_SIZE));
    const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 12)}`;
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(total));
        fd.append("filename", f.name);
        fd.append("file", blob, f.name);
        const { data } = await api.post("/admin/upload/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
        if (data.done) {
          onChange(data.url);
          toast.success(`${accept === "video" ? "Video" : "Image"} uploaded`);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    setProgress(null);
  };

  return (
    <div data-testid={testId}>
      <div className="flex gap-2">
        <input
          type="text"
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a URL or upload a file →"
          className="input-dark flex-1"
          data-testid={`${testId}-input`}
        />
        <input ref={inputRef} type="file" accept={acceptAttr} className="hidden" onChange={onFile} data-testid={`${testId}-file`} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={progress !== null}
          className="btn-glass !py-2.5 !px-4 shrink-0"
          data-testid={`${testId}-btn`}
        >
          <UploadCloud size={15} /> {progress !== null ? `${progress}%` : "Upload"}
        </button>
      </div>
      {progress !== null && (
        <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      {value && accept === "image" && value.trim() !== "" && (
        <img src={value} alt="preview" className="mt-2 h-16 rounded-lg border border-white/10 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
      )}
    </div>
  );
}
